import { useAuthStore } from "@/stores/auth-store";

/**
 * Live location updates for the Employee Tracking map, from
 * GET /location-tracking/live/stream (Server-Sent Events).
 *
 * fetch() + a stream reader instead of EventSource, because EventSource
 * cannot send the Authorization header and the access token must never go in
 * a URL. The server only sends events for employees the caller may see, and
 * ends each stream after about 100 seconds; this client reconnects, so a
 * refreshed access token is picked up on the next connection.
 */

export type LiveEventType = "location" | "session" | "status";

export interface LiveLocationEvent {
  type: LiveEventType;
  employee_id: string;
  latitude?: number;
  longitude?: number;
  accuracy_meters?: number;
  speed_mps?: number;
  heading_degrees?: number;
  recorded_at?: string;
  received_at?: string;
  battery_percent?: number;
  network_type?: string;
  session_id?: string;
  active?: boolean;
  session_started_at?: string;
  total_distance_meters?: number;
  device_status?: string;
  /** location events: the fix was reported as mocked (fake GPS). Omitted
   * when false. */
  is_mocked?: boolean;
}

/** connecting: first attempt. live: stream open. reconnecting: dropped,
 * retrying. unavailable: the server said no (feature off, no permission). */
export type LiveConnectionState = "connecting" | "live" | "reconnecting" | "unavailable";

export interface SSEMessage {
  event: string;
  data: string;
}

/**
 * Splits buffered SSE text into complete messages. Returns the messages and
 * the unfinished tail to keep for the next chunk. Comment lines (heartbeats)
 * and retry: lines are skipped.
 */
export function parseSSE(buffer: string): { messages: SSEMessage[]; rest: string } {
  const normalized = buffer.replace(/\r\n/g, "\n");
  const blocks = normalized.split("\n\n");
  const rest = blocks.pop() ?? "";
  const messages: SSEMessage[] = [];
  for (const block of blocks) {
    let event = "message";
    const data: string[] = [];
    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
    }
    if (data.length > 0) messages.push({ event, data: data.join("\n") });
  }
  return { messages, rest };
}

const STOP_STATUSES = new Set([403, 404, 422, 501]);

/**
 * Opens the stream and keeps it open until the returned function is called.
 * onAuthExpired is called on a 401 so the caller can trigger a normal API
 * request, which refreshes the access token, before the next attempt.
 */
export function openLiveLocationStream(handlers: {
  onEvent: (event: LiveLocationEvent) => void;
  onState: (state: LiveConnectionState) => void;
  onAuthExpired?: () => void;
}): () => void {
  const controller = new AbortController();
  let stopped = false;
  let delay = 3000;

  const run = async () => {
    handlers.onState("connecting");
    while (!stopped) {
      try {
        const { accessToken, impersonatedTenant } = useAuthStore.getState();
        const headers: Record<string, string> = { Accept: "text/event-stream" };
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
        if (impersonatedTenant) headers["X-Tenant-ID"] = impersonatedTenant.id;
        const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/location-tracking/live/stream`, {
          headers,
          signal: controller.signal,
          cache: "no-store",
        });
        if (STOP_STATUSES.has(res.status)) {
          handlers.onState("unavailable");
          return;
        }
        if (res.status === 401) {
          handlers.onAuthExpired?.();
          throw new Error("unauthorized");
        }
        if (!res.ok || !res.body) throw new Error(`stream status ${res.status}`);

        handlers.onState("live");
        delay = 3000;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSSE(buffer);
          buffer = parsed.rest;
          for (const message of parsed.messages) {
            if (message.event !== "location" && message.event !== "session" && message.event !== "status") continue;
            try {
              handlers.onEvent(JSON.parse(message.data) as LiveLocationEvent);
            } catch {
              // A malformed event is skipped; the next refetch corrects the map.
            }
          }
        }
        // The server ends each stream on purpose; reconnect straight away.
        if (!stopped) handlers.onState("reconnecting");
        continue;
      } catch {
        if (stopped) return;
        handlers.onState("reconnecting");
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = Math.min(delay * 2, 30_000);
    }
  };
  void run();

  return () => {
    stopped = true;
    controller.abort();
  };
}
